/**
 * Filter operator definitions — maps FilterOperatorId to Bitrix24 key prefixes and UI metadata.
 *
 * SOURCE: ENTITY_MODULE_EXPORT/lib/utils/filterOperators.ts
 * Adapted for Commission Manager: @/ path aliases replaced with relative imports.
 */

import { DATE_PERIOD_DEFS, isDatePeriodOperator } from "./datePeriods";
import { FIELD_TYPE_TO_CAT, getFieldTypeConfig, isFieldFilterable } from "./crmFieldsConfig";
import type { FieldCat } from "./crmFieldsConfig";
import type { FilterOperatorId, FilterRule } from "../types/filters";

// ── Operator definition ───────────────────────────────────────────────────────

export interface OperatorDef {
  id: FilterOperatorId;
  label: string;
  bitrixPrefix: string | null;
  valueShape: "scalar" | "array" | "range" | "none";
}

// ── OPERATOR_DEFS ─────────────────────────────────────────────────────────────

export const OPERATOR_DEFS: Partial<Record<FilterOperatorId, OperatorDef>> = {
  equal_to: { id: "equal_to", label: "Equal To", bitrixPrefix: "", valueShape: "scalar" },
  not_equal_to: {
    id: "not_equal_to",
    label: "Not Equal To",
    bitrixPrefix: "!=",
    valueShape: "scalar",
  },
  more_than: { id: "more_than", label: "More Than", bitrixPrefix: ">", valueShape: "scalar" },
  not_less_than: {
    id: "not_less_than",
    label: "Not Less Than",
    bitrixPrefix: ">=",
    valueShape: "scalar",
  },
  less_than: { id: "less_than", label: "Less Than", bitrixPrefix: "<", valueShape: "scalar" },
  not_more_than: {
    id: "not_more_than",
    label: "Not More Than",
    bitrixPrefix: "<=",
    valueShape: "scalar",
  },
  is_in: { id: "is_in", label: "Is In", bitrixPrefix: "@", valueShape: "array" },
  is_not_part_of: {
    id: "is_not_part_of",
    label: "Is Not Part Of",
    bitrixPrefix: "!@",
    valueShape: "array",
  },
  includes: { id: "includes", label: "Includes", bitrixPrefix: "%", valueShape: "scalar" },
  does_not_include: {
    id: "does_not_include",
    label: "Doesn't Include",
    bitrixPrefix: "!%",
    valueShape: "scalar",
  },
  not_empty: { id: "not_empty", label: "Not Empty", bitrixPrefix: "!=", valueShape: "none" },
  empty: { id: "empty", label: "Empty", bitrixPrefix: "", valueShape: "none" },
  in_range: { id: "in_range", label: "In Range", bitrixPrefix: null, valueShape: "range" },
};

// ── UNSUPPORTED_FILTER_TYPES ──────────────────────────────────────────────────

export const UNSUPPORTED_FILTER_TYPES = new Set<string>(
  Object.entries(FIELD_TYPE_TO_CAT)
    .filter(([, cat]) => !isFieldFilterable(cat))
    .map(([type]) => type),
);

export { isFieldFilterable } from "./crmFieldsConfig";

// ── Rule validity ─────────────────────────────────────────────────────────────

/**
 * Returns true when a FilterRule is complete enough to be compiled and saved.
 */
export function isRuleValid(rule: FilterRule): boolean {
  if (!rule.field) return false;

  if (isDatePeriodOperator(rule.operator)) {
    const periodDef = DATE_PERIOD_DEFS[rule.operator];
    if (!periodDef) return false;
    switch (periodDef.valueShape) {
      case "none":
        return true;
      case "n_days": {
        const v = rule.value;
        return (
          typeof v === "object" &&
          v !== null &&
          !Array.isArray(v) &&
          "n" in v &&
          typeof (v as { n: unknown }).n === "number" &&
          v.n >= 1
        );
      }
      case "exact_date":
        return typeof rule.value === "string" && rule.value.length > 0;
      case "month_year": {
        const v = rule.value;
        if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
        const mv = v as { month?: unknown; year?: unknown };
        return (
          typeof mv.month === "number" &&
          mv.month >= 1 &&
          mv.month <= 12 &&
          typeof mv.year === "number" &&
          mv.year > 1000
        );
      }
      case "quarter_year": {
        const v = rule.value;
        if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
        const qv = v as { quarter?: unknown; year?: unknown };
        return (
          typeof qv.quarter === "number" &&
          qv.quarter >= 1 &&
          qv.quarter <= 4 &&
          typeof qv.year === "number" &&
          qv.year > 1000
        );
      }
      case "year_only": {
        const v = rule.value;
        if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
        const yv = v as { year?: unknown };
        return typeof yv.year === "number" && yv.year > 1000;
      }
      case "date_range": {
        const v = rule.value;
        if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
        const dv = v as { from?: unknown; to?: unknown };
        return (
          typeof dv.from === "string" &&
          dv.from.length > 0 &&
          typeof dv.to === "string" &&
          dv.to.length > 0
        );
      }
    }
  }

  const opDef = OPERATOR_DEFS[rule.operator];
  if (!opDef) return false;
  switch (opDef.valueShape) {
    case "none":
      return true;
    case "scalar":
      return rule.value !== null && rule.value !== undefined && rule.value !== "";
    case "array":
      return Array.isArray(rule.value) && rule.value.length > 0;
    case "range": {
      if (!rule.value || typeof rule.value !== "object" || Array.isArray(rule.value)) return false;
      const rv = rule.value as { from: unknown; to: unknown };
      return (
        rv.from !== null &&
        rv.from !== undefined &&
        rv.from !== "" &&
        rv.to !== null &&
        rv.to !== undefined &&
        rv.to !== ""
      );
    }
  }
}

/**
 * Returns the display label for any FilterOperatorId, including DatePeriodId values.
 */
export function getOperatorLabel(op: FilterOperatorId): string {
  if (isDatePeriodOperator(op)) return DATE_PERIOD_DEFS[op].label;
  return OPERATOR_DEFS[op]?.label ?? op;
}

/**
 * Returns the operators available for a given field category.
 */
export function getOperatorsForFieldCat(cat: FieldCat): FilterOperatorId[] {
  return getFieldTypeConfig(cat).operators;
}
